import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Printer, FileText, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { reportService } from '../../services/reportService';
import { generateDailyCutPDF } from '../../utils/dailyCutPdfGenerator';
import { startOfDay, endOfDay, parseISO } from 'date-fns';

interface CashCutModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CashCutModal: React.FC<CashCutModalProps> = ({ isOpen, onClose }) => {
    const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);

    console.log('CashCutModal rendered, isOpen:', isOpen);

    if (!isOpen) return null;

    const handleGenerate = async () => {
        setLoading(true);
        const toastId = toast.loading('Obteniendo datos...');

        try {
            // Convert local date string to ISO range
            const dateObj = parseISO(selectedDate);
            const startISO = startOfDay(dateObj).toISOString();
            const endISO = endOfDay(dateObj).toISOString();

            // Fetch Sales and Payments for selected date with full range
            const [salesResponse, paymentsResponse] = await Promise.all([
                reportService.getReportSales({ startDate: startISO, endDate: endISO }),
                reportService.getReportPayments({ startDate: startISO, endDate: endISO })
            ]);

            if (salesResponse.success && paymentsResponse.success) {
                // Generate PDF using existing utility with the selected date
                generateDailyCutPDF(salesResponse.data, paymentsResponse.data, selectedDate);
                toast.success('Corte generado correctamente', { id: toastId });
                onClose();
            } else {
                toast.error('Error al obtener datos', { id: toastId });
            }
        } catch (error) {
            console.error('Error generating cash cut:', error);
            toast.error('Error al generar el corte', { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[1000] flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-gray-100"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                            <FileText className="w-5 h-5" />
                        </div>
                        <h3 className="font-bold text-gray-800">Corte de Caja</h3>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Seleccione la Fecha
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                <Calendar className="w-5 h-5" />
                            </div>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 shadow-sm transition-all"
                            />
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                            Se generará el reporte de ingresos y ventas acumulados durante todo el día seleccionado (00:00 - 23:59).
                        </p>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                        <h4 className="flex items-center text-sm font-bold text-blue-800 mb-2">
                            <Printer className="w-4 h-4 mr-2" />
                            Información Incluida
                        </h4>
                        <ul className="text-xs text-blue-700 space-y-1 list-disc pl-4">
                            <li>Total de ventas desglosado por método de pago</li>
                            <li>Listado detallado de transacciones</li>
                            <li>Apartados y abonos registrados</li>
                            <li>Métricas operativas del día</li>
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-200 rounded-lg transition-colors"
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleGenerate}
                        disabled={loading}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Generando...
                            </>
                        ) : (
                            <>
                                <Printer className="w-4 h-4 mr-2" />
                                Generar Corte
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};
